import { useEffect, useRef, useState } from "react";
import { withInboundProcessGuard } from "src/hocs/with-role-based-guard";
import { paths } from "src/paths";
import {
  Autocomplete,
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Container,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  TextField,
  Card,
  Divider,
  useMediaQuery,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Seo } from "src/components/seo";
import {
  CustomTextField,
  CustomTypography,
  CustomButton,
  CustomFormControl,
} from "src/components/inputs";
// Redux Toolkit
import { thunks } from "src/thunks/inbound-process";
import { slice } from "src/slices/inbound-process";
import { useDispatch, useSelector } from "src/store";
import { useAuth } from "src/hooks/use-auth";
import {
  CloseOutlined,
  KeyboardBackspace,
  Clear as ClearIcon,
  PlaylistAdd,
} from "@mui/icons-material";
import { groupTypes } from "src/guards/role-based-guard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import CardContent from "@mui/material/CardContent";
import { styled } from "@mui/material/styles";
import { PropertyList } from "src/components/property-list";
import { PropertyListItem } from "src/components/property-list-item";
import moment from "moment";
import * as XLSX from "xlsx";
import camelCase from "lodash.camelcase";
import toast from "react-hot-toast";
import { GetApp, FileUpload } from "@mui/icons-material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomCalendarIcon from "src/icons/tmw-ui/tmw-Calendar-Symbol-Option-2.png";

const AddInboundRequestPage = ({ inboundType }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const params = useParams();
  const selectPOModalRef = useRef(null);
  const zedonkFileInputRef = useRef(null);
  const [cancelButtonClicked, setCancelButtonClicked] = useState(false);

  const userGroup = user?.group?.key;

  // Params
  const purchaseOrderId = params.purchase_order_id;

  const {
    POIsValid,
    POVerificationLoading,
    verifiedPO,
    purchaseOrder,
    purchaseOrders,
    POItems,
    POItemsLoading,
    submitInboundRequestLoading,
    documentNumber,
    submitInboundRequestError,
    submitCurrencyError,
    inboundRequestsItemsQuantityOfPO,
    saveChangesLoading,
    pickupAutocompleteData,
    supplierAddresses,
    supplierAddressesLoading,
  } = useSelector((state) => state.inboundProcess);

  const isInvestedBrand = purchaseOrder?.brandType === "Invested";
  const showToleranceColumns =
    isInvestedBrand && userGroup !== groupTypes.supplier;

  const [selectedPO, setSelectedPO] = useState(purchaseOrderId || "");
  const [selectPurchaseOrderModalVisible, setSelectPurchaseOrderModalVisible] =
    useState(false);
  const [selectedPOItems, setSelectedPOItems] = useState([]);
  const [
    selectPurchaseOrderItemsModalVisible,
    setSelectPurchaseOrderItemsModalVisible,
  ] = useState(purchaseOrderId ? true : false);
  const [currentModalSection, setCurrentModalSection] = useState("po_items");
  const [quantityValues, setQuantityValues] = useState({});
  const [selectAllItemsChecked, setSelectAllItemsChecked] = useState(false);
  const [searchPOItem, setSearchPOItem] = useState("");
  const [filteredPOItems, setFilteredPOItems] = useState([]);
  const [importZedonkParsing, setImportZedonkParsing] = useState(false);
  const [confirmIRModalOpened, setConfirmIRModalOpened] = useState(false);
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [poSummaryExpanded, setPoSummaryExpanded] = useState(false);
  // Short shipment answers (Invested brands only)
  const [shortShipmentAnswers, setShortShipmentAnswers] = useState({});
  // Total units (qty) input and validation
  const [totalUnits, setTotalUnits] = useState("");
  const [totalUnitsError, setTotalUnitsError] = useState(
    "Total units (qty) is required.",
  );
  // Autocomplete field-local states
  const [autocompleteStates, setAutocompleteStates] = useState({});

  // Pickup Autocomplete Data
  useEffect(() => {
    dispatch(thunks.getPickupAutocompleteData(null));
  }, []);

  useEffect(() => {
    if (userGroup === groupTypes.supplier) {
      dispatch(thunks.getSupplierAddresses());
    } else if (userGroup === groupTypes.brand) {
      dispatch(thunks.getSupplierAddressesByPurchaseOrder(purchaseOrderId));
    }
  }, [userGroup]);

  useEffect(() => {
    if (supplierAddresses) {
      setPickupCompanyName(supplierAddresses?.companyName || "");

      const combinedAddress =
        (supplierAddresses?.addr1 || "") +
        (supplierAddresses?.addr2
          ? supplierAddresses?.addr1
            ? " " + supplierAddresses?.addr2
            : supplierAddresses?.addr2
          : "");
      setPickupAddress(combinedAddress || "");
      setPickupZipCode(supplierAddresses?.zip || "");
      setPickupCity(supplierAddresses?.city || "");
      setPickupCountry(supplierAddresses?.country || "");
    }
  }, [supplierAddresses]);

  // General filter PO Items
  useEffect(() => {
    const auxFilteredPOItems = POItems.filter((POItem) => {
      const search = searchPOItem.toLowerCase();
      return (
        POItem.description?.toLowerCase().includes(search) ||
        POItem.colourDescription?.toLowerCase().includes(search) ||
        POItem.itemSku?.toLowerCase().includes(search) ||
        POItem.internalId?.toLowerCase().includes(search) ||
        POItem.vendorCode?.toLowerCase().includes(search)
      );
    });

    setFilteredPOItems(auxFilteredPOItems);
  }, [POItems, searchPOItem]);

  //
  // FILE SELECTS
  //

  // Packing List
  const [packingListFileName, setPackingListFileName] = useState("");
  const [packingListFileData, setPackingListFileData] = useState(null);
  // Invoice
  const [invoiceFileName, setInvoiceFileName] = useState("");
  const [invoiceFileData, setInvoiceFileData] = useState(null);
  // Currency
  const [invoiceNetTotal, setInvoiceNetTotal] = useState("");
  const [invoiceNetTotalError, setInvoiceNetTotalError] = useState("");

  // Pickup Fields
  const [pickupConfirmationNumber, setPickupConfirmationNumber] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState(new Date());
  const [numberOfBoxes, setNumberOfBoxes] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryNumber, setDeliveryNumber] = useState("");
  const [totalNumberOfCartons, setTotalNumberOfCartons] = useState("");
  const [totalNumberOfPallets, setTotalNumberOfPallets] = useState("");
  const [boxDimensions, setBoxDimensions] = useState("");
  const [palletDimensions, setPalletDimensions] = useState("");
  const [totalGrossWeight, setTotalGrossWeight] = useState("");
  const [totalGrossWeightError, setTotalGrossWeightError] = useState("");
  const [totalNetWeight, setTotalNetWeight] = useState("");
  const [totalNetWeightError, setTotalNetWeightError] = useState("");
  const [pickupCompanyName, setPickupCompanyName] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupZipCode, setPickupZipCode] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [pickupCountry, setPickupCountry] = useState("");
  const [openingHours, setOpeningHours] = useState("");
  const [contactName, setContactName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [supplierEmailContact, setSupplierEmailContact] = useState("");
  const [contentDetails, setContentDetails] = useState("");
  const [packingListNumber, setPackingListNumber] = useState("");
  const [comments, setComments] = useState("");
  const [readyDateForPickup, setReadyDateForPickup] = useState("");

  const invoiceNetTotalValidation = (value) => {
    const raw = "" + value ?? "";
    const normalized = raw.replace(",", ".");
    setInvoiceNetTotal(normalized);

    if (!normalized) {
      setInvoiceNetTotalError("Invoice Net Total is required.");
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      setInvoiceNetTotalError(
        "Invalid number. Use '.' for decimals (e.g., 1234.56).",
      );
    } else {
      setInvoiceNetTotalError("");
    }
  };

  const totalGrossWeightValidation = (value, netWeight = totalNetWeight) => {
    const raw = "" + (value ?? "");
    const normalized = raw.replace(",", ".");
    setTotalGrossWeight(normalized);

    if (!normalized) {
      setTotalGrossWeightError("Total Gross Weight is required.");
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      setTotalGrossWeightError(
        "Invalid number. Use '.' for decimals (e.g., 1234.56).",
      );
    } else {
      const grossNum = parseFloat(normalized);
      const netNum = parseFloat(netWeight?.replace(",", ".") || "0");
      if (netWeight && !isNaN(netNum) && grossNum < netNum) {
        setTotalGrossWeightError("Gross Weight must be >= Net Weight.");
      } else {
        setTotalGrossWeightError("");
      }
    }

    // Also revalidate net weight when gross changes
    if (netWeight && !/^\d+(?:\.\d{1,2})?$/.test(netWeight.replace(",", "."))) {
      // Net weight has its own format error, don't change it
    } else if (netWeight) {
      const grossNum = parseFloat(normalized || "0");
      const netNum = parseFloat(netWeight.replace(",", ".") || "0");
      if (!isNaN(grossNum) && !isNaN(netNum) && grossNum < netNum) {
        setTotalNetWeightError("Net Weight must be <= Gross Weight.");
      } else if (
        totalNetWeightError === "Net Weight must be <= Gross Weight."
      ) {
        setTotalNetWeightError("");
      }
    }
  };

  const totalNetWeightValidation = (value, grossWeight = totalGrossWeight) => {
    const raw = "" + (value ?? "");
    const normalized = raw.replace(",", ".");
    setTotalNetWeight(normalized);

    if (!normalized) {
      setTotalNetWeightError("Total Net Weight is required.");
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      setTotalNetWeightError(
        "Invalid number. Use '.' for decimals (e.g., 1234.56).",
      );
    } else {
      const netNum = parseFloat(normalized);
      const grossNum = parseFloat(grossWeight?.replace(",", ".") || "0");
      if (grossWeight && !isNaN(grossNum) && netNum > grossNum) {
        setTotalNetWeightError("Net Weight must be <= Gross Weight.");
      } else {
        setTotalNetWeightError("");
      }
    }

    // Also revalidate gross weight when net changes
    if (
      grossWeight &&
      !/^\d+(?:\.\d{1,2})?$/.test(grossWeight.replace(",", "."))
    ) {
      // Gross weight has its own format error, don't change it
    } else if (grossWeight) {
      const netNum = parseFloat(normalized || "0");
      const grossNum = parseFloat(grossWeight.replace(",", ".") || "0");
      if (!isNaN(grossNum) && !isNaN(netNum) && grossNum < netNum) {
        setTotalGrossWeightError("Gross Weight must be >= Net Weight.");
      } else if (
        totalGrossWeightError === "Gross Weight must be >= Net Weight."
      ) {
        setTotalGrossWeightError("");
      }
    }
  };

  const roundUpToNextQuarterHour = (date) => {
    const roundedDate = new Date(date);
    const minutes = date.getMinutes();
    const remainder = minutes % 15;

    if (remainder !== 0) {
      roundedDate.setMinutes(minutes + (15 - remainder));
    }

    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    return new Date(roundedDate);
  };

  useEffect(() => {
    if (!purchaseOrderId) {
      setSelectPurchaseOrderModalVisible(true);
      if (userGroup === groupTypes.supplier) {
        dispatch(thunks.getPurchaseOrdersSupplier());
      } else if (userGroup === groupTypes.brand) {
        dispatch(thunks.getPurchaseOrdersBrand());
      } else {
        dispatch(thunks.getPurchaseOrders());
      }
    } else {
      dispatch(thunks.verifyPurchaseOrderHash(selectedPO));
    }
  }, [purchaseOrderId]);

  useEffect(() => {
    if (verifiedPO && POIsValid !== undefined) {
      dispatch(slice.actions.setVerifiedPO(false));

      if (!POIsValid) {
        window.location.href =
          (inboundType === "requests"
            ? paths.dashboard.inbound.requests.index
            : paths.dashboard.inbound.asn.index) + "/404";
      }
    }
  }, [verifiedPO]);

  useEffect(() => {
    if (POIsValid) {
      dispatch(thunks.getPOItems(selectedPO));
      dispatch(thunks.getPurchaseOrder(selectedPO));
    }
  }, [POIsValid]);

  useEffect(() => {
    if (purchaseOrder?.id) {
      dispatch(thunks.getInboundRequestsItemsQuantityOfPO(purchaseOrder.id));
    }
  }, [purchaseOrder]);

  useEffect(() => {
    if (!purchaseOrder) return;

    if (invoiceNetTotal === "") {
      setInvoiceNetTotal(0);
    }

    invoiceNetTotalValidation(invoiceNetTotal);
  }, [purchaseOrder, invoiceNetTotal]);

  useEffect(() => {
    setQuantityValues((prevValues) => {
      const newValues = { ...prevValues };

      filteredPOItems.forEach((POItem) => {
        if (newValues[POItem.internalId] === undefined) {
          newValues[POItem.internalId] = POItem.remainingQuantity;
        }
      });

      return newValues;
    });
  }, [filteredPOItems]);

  useEffect(() => {
    if (selectAllItemsChecked) {
      const auxFilteredPOItems = filteredPOItems.filter((POItem) => {
        // For Invested brands, only filter out cancelled items
        if (purchaseOrder?.brandType === "Invested") {
          return !POItem.isCancelled;
        }
        // For non-Invested brands, keep original logic
        return !POItem.isCancelled && POItem.remainingQuantity > 0;
      });
      setSelectedPOItems(auxFilteredPOItems);
    } else {
      setSelectedPOItems([]);
    }
  }, [selectAllItemsChecked, purchaseOrder?.brandType]);

  // Cleanup short shipment answers for items that are no longer short (Invested only)
  useEffect(() => {
    if (purchaseOrder?.brandType !== "Invested") return;
    if (POItems.length === 0) return;

    setShortShipmentAnswers((previousAnswers) => {
      let changed = false;
      const nextAnswers = { ...previousAnswers };

      selectedPOItems.forEach((poItem) => {
        const foundPOItem = POItems.find(
          (poItem2) => poItem2.internalId === poItem.internalId,
        );
        if (!foundPOItem) return;
        const qtyLeft = Number(foundPOItem.remainingQuantity);

        const hasExplicitQty =
          Object.prototype.hasOwnProperty.call(
            quantityValues,
            poItem.internalId,
          ) &&
          quantityValues[poItem.internalId] !== "" &&
          !isNaN(Number(quantityValues[poItem.internalId]));

        const currentNew = hasExplicitQty
          ? Number(quantityValues[poItem.internalId])
          : qtyLeft;
        const isShort = currentNew < qtyLeft && !poItem.isCancelled;

        if (!isShort && nextAnswers[poItem.internalId] !== undefined) {
          delete nextAnswers[poItem.internalId];
          changed = true;
        }
      });

      return changed ? nextAnswers : previousAnswers;
    });
  }, [POItems, quantityValues, selectedPOItems, purchaseOrder?.brandType]);

  useEffect(() => {
    if (!totalUnits) return;

    const calculatedTotalUnits = selectedPOItems.reduce((sum, item) => {
      const qty = quantityValues[item.internalId];
      return sum + (qty && !isNaN(Number(qty)) ? Number(qty) : 0);
    }, 0);

    if (Number(totalUnits) !== calculatedTotalUnits) {
      setTotalUnitsError("Value must equal the Total Quantity.");
    } else {
      setTotalUnitsError("");
    }
  }, [totalUnits, quantityValues, selectedPOItems]);

  const renderSelectPurchaseOrderModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          ref={selectPOModalRef}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "50vw",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "green",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px" }}>
                <CustomTypography message="Please select the Purchase Order you want to create an Inbound Request for:" />
                <Stack
                  style={{
                    width: "100%",
                    alignItems: "center",
                    marginTop: "25px",
                  }}
                >
                  <Autocomplete
                    id="autocomplete-purchaseOrder"
                    name="autocomplete-purchaseOrder"
                    options={purchaseOrders}
                    getOptionLabel={(option) =>
                      `${option.season} | ${option.brandName} | ${option.documentNumber} | ${option.supplier}`
                    }
                    value={
                      purchaseOrders.find(
                        (po) => po.internalId === selectedPO,
                      ) || null
                    }
                    onChange={(_, value) =>
                      setSelectedPO(value ? value.internalId : "")
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.internalId}>
                        <div
                          style={{
                            fontFamily: "tmwCustomFont",
                            letterSpacing: 2,
                            fontSize: 11,
                          }}
                        >
                          {option.season} | {option.brandName} |{" "}
                          {option.documentNumber} | {option.supplier}
                        </div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          "& .MuiInput-underline:before": {
                            borderBottom: "1px solid #E6E6E6",
                          },
                          "& .MuiInput-underline:after": {
                            borderBottom: "2px solid black",
                          },
                          "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                            cursor: "pointer",
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "tmwCustomFont",
                            paddingLeft: "10px",
                          },
                          padding: "10px 0",
                          width: "100%",
                        }}
                        id="selectedPurchaseOrder"
                        name="selectedPurchaseOrder"
                        fullWidth
                        variant="standard"
                      />
                    )}
                    style={{ width: "100%" }}
                  />
                </Stack>
                <Stack
                  style={{
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 10,
                    marginTop: "15px",
                  }}
                >
                  {purchaseOrderId ? (
                    <CustomButton
                      label="Cancel"
                      style={{ width: "100px" }}
                      onClick={() => setSelectPurchaseOrderModalVisible(false)}
                    />
                  ) : (
                    <CustomButton
                      label="Cancel"
                      style={{ width: "100px" }}
                      onClick={() =>
                        (window.location.href =
                          inboundType === "requests"
                            ? paths.dashboard.inbound.requests.index
                            : paths.dashboard.inbound.asn.index)
                      }
                    />
                  )}
                  <CustomButton
                    label="Select"
                    style={{ width: "100px" }}
                    disabled={!selectedPO}
                    onClick={() => {
                      window.location.href =
                        (inboundType === "requests"
                          ? paths.dashboard.inbound.requests.index
                          : paths.dashboard.inbound.asn.index) +
                        "/new/" +
                        selectedPO;
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const hasAnyItemOutsideToleranceForInvested = () => {
    if (purchaseOrder?.brandType !== "Invested") {
      return false;
    }

    return selectedPOItems.some((poItem) => {
      const permittedDisplay = poItem.displayTolerance;
      const permittedNumeric = Number(
        poItem.acceptedToleranceAmount ?? permittedDisplay ?? 0,
      );

      const baseDiff = Number(poItem.difference ?? 0);
      // For new IR, currentOld is always 0 since there's no existing IR
      const currentOld = 0;
      const hasExplicitQty =
        Object.prototype.hasOwnProperty.call(
          quantityValues,
          poItem.internalId,
        ) &&
        quantityValues[poItem.internalId] !== "" &&
        !isNaN(Number(quantityValues[poItem.internalId]));
      const currentNew = hasExplicitQty
        ? Number(quantityValues[poItem.internalId])
        : Number(poItem.remainingQuantity);

      // For new IR: adjustedDiff = -(baseDiff + currentNew)
      const adjustedDiff = -(baseDiff + currentNew);
      const isOutsideTolerance =
        Math.abs(adjustedDiff) > Math.abs(permittedNumeric);

      const qtyLeft = Number(poItem.remainingQuantity);
      const readyToShipQty = hasExplicitQty
        ? Number(quantityValues[poItem.internalId])
        : qtyLeft;

      const shortAnswer = shortShipmentAnswers[poItem.internalId];

      const isCase1 =
        readyToShipQty > qtyLeft &&
        isOutsideTolerance; /* Ready To Ship > Pending Qty AND outside tolerance */

      const isCase2 =
        readyToShipQty < qtyLeft &&
        shortAnswer === "Yes" &&
        isOutsideTolerance; /* Ready To Ship < Pending Qty AND Short_Shipment = Yes AND outside tolerance */

      return isCase1 || isCase2;
    });
  };

  const handleCreateInboundRequest = (handleClose) => {
    const currency =
      purchaseOrder?.currency === "EUR"
        ? "€"
        : purchaseOrder?.currency === "GBP"
          ? "£"
          : purchaseOrder?.currency === "USD"
            ? "$"
            : purchaseOrder?.currency === "JPY"
              ? "¥"
              : "";

    const formattedSelectedPOItems = selectedPOItems.map((poItem) => ({
      ...poItem,
      strapiId: poItem.id,
      id: poItem.internalId,
      selectedQty: quantityValues[poItem.internalId],
      shortShipmentAnswer: shortShipmentAnswers[poItem.internalId],
    }));

    const formData = new FormData();

    // Append fields
    formData.append("author", user.email);
    formData.append("purchaseOrderId", purchaseOrderId);
    formData.append("documentNumber", documentNumber);

    // Append PO Items
    formData.append("poItems", JSON.stringify(formattedSelectedPOItems));

    // Append files
    formData.append("packingList", packingListFileData);
    formData.append("invoice", invoiceFileData);

    // Append Invoice Net Total
    formData.append("invoiceNetTotal", invoiceNetTotal);
    formData.append("totalUnits", totalUnits);

    // Append Pickup Fields
    formData.append("pickupDate", pickupDate);
    formData.append("invoiceNumber", invoiceNumber);
    formData.append("deliveryAddress", deliveryAddress);
    formData.append("deliveryNumber", deliveryNumber);
    formData.append("totalNumberOfCartons", totalNumberOfCartons);
    formData.append("totalNumberOfPallets", totalNumberOfPallets);
    formData.append("boxDimensions", boxDimensions);
    formData.append("palletDimensions", palletDimensions);
    formData.append("totalGrossWeight", totalGrossWeight);
    formData.append("totalNetWeight", totalNetWeight);
    formData.append("pickupCompanyName", pickupCompanyName);
    formData.append("pickupAddress", pickupAddress);
    formData.append("pickupZipCode", pickupZipCode);
    formData.append("pickupCity", pickupCity);
    formData.append("pickupCountry", pickupCountry);
    formData.append("openingHours", openingHours);
    formData.append("contactName", contactName);
    formData.append("phoneNumber", phoneNumber);
    formData.append("supplierEmailContact", supplierEmailContact);
    formData.append("contentDetails", contentDetails);
    formData.append("packingListNumber", packingListNumber);
    formData.append("comments", comments);
    formData.append("readyDateForPickup", readyDateForPickup);

    // For Invested brands: if all items are within tolerance, skip Pending and go directly to Authorized
    const isInvestedBrand = purchaseOrder?.brandType === "Invested";
    if (isInvestedBrand && !hasAnyItemOutsideToleranceForInvested()) {
      formData.append("skipPending", "true");
    }

    dispatch(thunks.createInboundRequest(formData, handleClose));

    handleClose();
  };

  const renderConfirmIRModal = (
    handleConfirm,
    handleCancel,
    message1,
    submitLoader,
  ) => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "50vw",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "green",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <Stack style={{ padding: "35px", gap: 15 }}>
                  {message1 && <CustomTypography message={message1} />}
                </Stack>
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                    margin: "-15px 25px 25px 0",
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={handleCancel}
                    disabled={submitLoader}
                  />
                  <CustomButton
                    label="Confirm"
                    onClick={handleConfirm}
                    disabled={submitLoader}
                    loader={
                      submitLoader && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const handleSaveChanges = () => {
    const formattedSelectedPOItems = selectedPOItems.map((poItem) => ({
      ...poItem,
      strapiId: poItem.id,
      id: poItem.internalId,
      selectedQty: quantityValues[poItem.internalId],
      shortShipmentAnswer: shortShipmentAnswers[poItem.internalId],
    }));

    const formData = new FormData();

    // Append fields
    formData.append("author", user.email);
    formData.append("purchaseOrderId", purchaseOrderId);
    formData.append("documentNumber", documentNumber);

    // Append PO Items
    formData.append("poItems", JSON.stringify(formattedSelectedPOItems));

    // Append files
    formData.append("packingList", packingListFileData);
    formData.append("invoice", invoiceFileData);

    // Append Invoice Net Total
    formData.append("invoiceNetTotal", invoiceNetTotal);
    formData.append("totalUnits", totalUnits);

    // Append Pickup Fields
    formData.append("pickupDate", pickupDate);
    formData.append("invoiceNumber", invoiceNumber);
    formData.append("deliveryAddress", deliveryAddress);
    formData.append("deliveryNumber", deliveryNumber);
    formData.append("totalNumberOfCartons", totalNumberOfCartons);
    formData.append("totalNumberOfPallets", totalNumberOfPallets);
    formData.append("boxDimensions", boxDimensions);
    formData.append("palletDimensions", palletDimensions);
    formData.append("totalGrossWeight", totalGrossWeight);
    formData.append("totalNetWeight", totalNetWeight);
    formData.append("pickupCompanyName", pickupCompanyName);
    formData.append("pickupAddress", pickupAddress);
    formData.append("pickupZipCode", pickupZipCode);
    formData.append("pickupCity", pickupCity);
    formData.append("pickupCountry", pickupCountry);
    formData.append("openingHours", openingHours);
    formData.append("contactName", contactName);
    formData.append("phoneNumber", phoneNumber);
    formData.append("supplierEmailContact", supplierEmailContact);
    formData.append("contentDetails", contentDetails);
    formData.append("packingListNumber", packingListNumber);
    formData.append("comments", comments);
    formData.append("readyDateForPickup", readyDateForPickup);

    dispatch(thunks.saveChangesAddInboundRequest(formData));
  };

  const totalNumberOfCartonsValid = !(
    totalNumberOfCartons === null ||
    totalNumberOfCartons === undefined ||
    totalNumberOfCartons === "" ||
    isNaN(Number(totalNumberOfCartons)) ||
    Number(totalNumberOfCartons) < 0
  );

  const totalNumberOfPalletsValid = !(
    totalNumberOfPallets === null ||
    totalNumberOfPallets === undefined ||
    totalNumberOfPallets === "" ||
    isNaN(Number(totalNumberOfPallets)) ||
    Number(totalNumberOfPallets) < 0
  );

  const poIncotermFromPO =
    purchaseOrder?.incoterm || purchaseOrder?.shippingTerms || "";
  const isDDPIncoterm = poIncotermFromPO === "DDP";

  const renderNewInboundRequestButtons = () => {
    const pickupLocationValid =
      pickupCountry &&
      pickupCity &&
      pickupZipCode &&
      pickupAddress &&
      pickupCompanyName;

    const scheduleAndContactValid =
      readyDateForPickup &&
      openingHours &&
      contactName &&
      phoneNumber &&
      supplierEmailContact;

    const shippingDetailsByIncotermValid = isDDPIncoterm
      ? pickupLocationValid
      : pickupLocationValid && scheduleAndContactValid;

    const submitButtonDisabled = !(
      packingListFileData &&
      invoiceFileData &&
      invoiceNetTotal &&
      invoiceNumber &&
      packingListNumber &&
      totalUnits &&
      !totalUnitsError &&
      totalNumberOfCartonsValid &&
      totalNumberOfPalletsValid &&
      boxDimensions &&
      palletDimensions &&
      totalGrossWeight &&
      !totalGrossWeightError &&
      totalNetWeight &&
      !totalNetWeightError &&
      contentDetails &&
      shippingDetailsByIncotermValid
    );

    return (
      <Stack
        style={{
          position: "fixed",
          right: 0,
          bottom: 0,
          flexDirection: "row",
          gap: 10,
          justifyContent: "flex-end",
          zIndex: 500,
          padding: "25px",
        }}
      >
        <CustomButton
          style={{ background: "white" }}
          label="Save Changes"
          onClick={handleSaveChanges}
          loader={
            saveChangesLoading && (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginLeft: "10px" }}
              />
            )
          }
        />
        <CustomButton
          style={{
            borderColor: submitCurrencyError ? "red" : "",
            background: "white",
          }}
          redButton={submitInboundRequestError}
          label="Submit Request"
          disabled={submitButtonDisabled}
          onClick={() => setConfirmIRModalOpened(true)}
          loader={
            submitInboundRequestLoading && (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginLeft: "10px" }}
              />
            )
          }
        />
        <CustomButton
          style={{ background: "white" }}
          label="Cancel"
          onClick={() =>
            (window.location.href =
              inboundType === "requests"
                ? paths.dashboard.inbound.requests.index
                : paths.dashboard.inbound.asn.index)
          }
        />
      </Stack>
    );
  };

  const renderInputSelect = (
    inputName,
    fileName,
    setFileName,
    fileData,
    setFileData,
    allowedFiles,
  ) => {
    const handleFileChange = (event) => {
      const file = event.target.files[0];

      if (file) {
        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (allowedFiles.includes(fileExtension)) {
          setFileName(file.name);
          setFileData(file);
        } else {
          alert("Invalid file type! Please upload a PDF, Excel, or Word file.");

          event.target.value = "";
          setFileName("");
          setFileData(null);
        }
      }
    };

    return (
      <Stack
        style={{
          alignItems: "center",
          flexDirection: "row",
          gap: 10,
        }}
      >
        <CustomButton
          component="label"
          disabled={!!(fileName || fileData)}
          label={
            <>
              {inputName}
              <Input
                type="file"
                id={inputName}
                accept={allowedFiles.map((ext) => `.${ext}`).join(", ")}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </>
          }
          style={{ width: "fit-content" }}
        />
        {fileName && (
          <Stack
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 5,
              margin: "0 10px",
              fontFamily: "tmwCustomFont",
              fontSize: 12,
              textDecoration: "underline",
            }}
          >
            {fileName}
            <ButtonBase
              onClick={() => {
                setFileName("");
                setFileData(null);
                dispatch(slice.actions.setSubmitCurrencyError(false));

                // Reset the file input on close
                const fileInput = document.getElementById(inputName);
                if (fileInput) {
                  fileInput.value = "";
                }
              }}
            >
              <CloseOutlined style={{ width: "18px" }} />
            </ButtonBase>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderCurrencySelect = () => {
    const currency =
      purchaseOrder?.currency === "EUR"
        ? "€"
        : purchaseOrder?.currency === "GBP"
          ? "£"
          : purchaseOrder?.currency === "USD"
            ? "$"
            : purchaseOrder?.currency === "JPY"
              ? "¥"
              : "";

    return (
      <>
        <Stack
          style={{
            width: "80%",
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <CustomTypography
            message="Invoice Net Total:"
            style={{ fontSize: 11, width: "240px" }}
          />
          <CustomTextField
            style={{ width: "100%" }}
            value={invoiceNetTotal || ""}
            inputProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">{currency}</InputAdornment>
                ),
              },
            }}
            handleChange={(event) => {
              invoiceNetTotalValidation(event.target.value);
              dispatch(slice.actions.setSubmitCurrencyError(false));
            }}
            type="number"
            error={!!invoiceNetTotalError}
            helperText={invoiceNetTotalError}
          />
        </Stack>
        {submitCurrencyError && (
          <Stack
            style={{
              fontFamily: "tmwCustomFont",
              fontSize: 11,
              color: "red",
            }}
          >
            <span>
              Invoice Net Total does not match the calculated total for the
              selected items.
            </span>
            <span>
              Please revise the total and possibly correct also the invoice.
            </span>
          </Stack>
        )}
      </>
    );
  };

  const renderNewInboundRequestContent = () => {
    return (
      <Stack
        style={{
          margin: "15px 0",
          padding: " 0 15px 25px 15px",
          alignItems: "flex-start",
          gap: 25,
        }}
      >
        <Stack
          style={{
            position: "relative",
            width: "100%",
            padding: "25px",
            border: "1px solid #E6E6E6",
          }}
        >
          <Stack style={{ position: "absolute", right: 25 }}>
            <CustomButton
              label="Edit Items"
              style={{ background: "white" }}
              onClick={() => {
                dispatch(slice.actions.setSubmitCurrencyError(false));
                setCurrentModalSection("po_items");
                setSelectPurchaseOrderItemsModalVisible(true);
              }}
            />
          </Stack>
          <CustomTypography
            message="Purchase Order Items:"
            title
            style={{ marginBottom: "35px" }}
          />
          <Stack style={{ width: "100%", alignItems: "center" }}>
            <Stack
              style={{
                display: "flex",
                width: "100%",
                gap: 10,
                position: "relative",
              }}
            >
              {/* Header */}
              <Stack
                style={{
                  flexDirection: "row",
                  fontWeight: "bold",
                  borderBottom: "1px solid #E6E6E6",
                  paddingBottom: 5,
                  fontSize: 10,
                  alignItems: "flex-start",
                }}
              >
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 0,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Internal ID
                </Stack>
                <Stack
                  style={{
                    flex: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  SKU
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  EAN Code
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Unit Cost
                </Stack>
                <Stack
                  style={{
                    flex: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Colour Description
                </Stack>
                <Stack
                  style={{
                    flex: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Description
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Vendor Code
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  PO Qty
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  {purchaseOrder?.brandType === "Invested"
                    ? "Pending"
                    : "Pending Qty"}
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  Ready To Ship
                </Stack>
                {showToleranceColumns && (
                  <Stack
                    style={{
                      flex: 1,
                      paddingLeft: 8,
                      paddingRight: 8,
                      minWidth: 0,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      textAlign: "right",
                    }}
                  >
                    {purchaseOrder?.brandType === "Invested"
                      ? "Tolerance"
                      : "Max Tolerance"}
                  </Stack>
                )}
                {showToleranceColumns && (
                  <>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {purchaseOrder?.brandType === "Invested"
                        ? "Remaining"
                        : "Remaining Qty"}
                    </Stack>
                    {purchaseOrder?.brandType === "Invested" && (
                      <Stack
                        style={{
                          flex: 1,
                          paddingLeft: 8,
                          paddingRight: 8,
                          minWidth: 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "right",
                        }}
                      >
                        Discrepancy
                      </Stack>
                    )}
                  </>
                )}
                {purchaseOrder?.brandType === "Invested" &&
                  !!Object.entries(shortShipmentAnswers)?.length && (
                    <Stack
                      style={{
                        flex: 1,
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 0,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Short Shipment (Yes/No)
                    </Stack>
                  )}
              </Stack>

              {/* POItems */}
              {selectedPOItems.map((POItem, index) => {
                const foundItemsQuantity = quantityValues[POItem.internalId];

                return (
                  <Stack
                    key={`po-item-${index}`}
                    style={{
                      flexDirection: "row",
                      fontSize: 15,
                      alignItems: "flex-start",
                      minHeight: "40px",
                    }}
                  >
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 0,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.internalId}
                    </Stack>
                    <Stack
                      style={{
                        flex: 2,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.itemSku}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.eanCode}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.rate}
                    </Stack>
                    <Stack
                      style={{
                        flex: 2,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.colourDescription}
                    </Stack>
                    <Stack
                      style={{
                        flex: 2,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.description}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.vendorCode}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {POItem.qty}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {POItem.remainingQuantity}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {foundItemsQuantity}
                    </Stack>
                    {showToleranceColumns && (
                      <Stack
                        style={{
                          flex: 1,
                          paddingLeft: 8,
                          paddingRight: 8,
                          minWidth: 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "right",
                        }}
                      >
                        {(() => {
                          const permittedNumeric = Number(
                            POItem.acceptedToleranceAmount ?? 0,
                          );
                          return <span>{permittedNumeric}</span>;
                        })()}
                      </Stack>
                    )}
                    {showToleranceColumns && (
                      <>
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            minWidth: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {(() => {
                            const permittedNumeric = Number(
                              POItem.acceptedToleranceAmount ?? 0,
                            );
                            const baseDiff = Number(POItem.difference ?? 0);
                            const currentNew = Number(
                              quantityValues[POItem.internalId] ??
                                POItem.remainingQuantity,
                            );
                            const adjustedDiff = Math.max(
                              0,
                              -(baseDiff + currentNew),
                            );
                            return <span>{adjustedDiff}</span>;
                          })()}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            paddingLeft: 8,
                            paddingRight: 8,
                            minWidth: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "right",
                          }}
                        >
                          {(() => {
                            const permittedNumeric = Number(
                              POItem.acceptedToleranceAmount ?? 0,
                            );
                            const readyToShip = Number(
                              quantityValues[POItem.internalId] ??
                                POItem.remainingQuantity,
                            );
                            const pendingQty = Number(POItem.remainingQuantity);

                            // Calculate Discrepancy
                            let discrepancy = 0;
                            if (readyToShip > pendingQty) {
                              // Overshipment
                              discrepancy =
                                readyToShip - (pendingQty + permittedNumeric);
                              // Only show if tolerance is exceeded
                              if (discrepancy <= 0) discrepancy = 0;
                            } else if (readyToShip < pendingQty) {
                              // Undershipment
                              discrepancy =
                                readyToShip - (pendingQty - permittedNumeric);
                              // Only show if tolerance is exceeded
                              if (discrepancy >= 0) discrepancy = 0;
                            }

                            return (
                              <span
                                style={{
                                  color: discrepancy !== 0 ? "red" : "inherit",
                                }}
                              >
                                {discrepancy}
                              </span>
                            );
                          })()}
                        </Stack>
                      </>
                    )}
                    {purchaseOrder?.brandType === "Invested" &&
                      !!Object.entries(shortShipmentAnswers)?.length && (
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "center",
                            paddingLeft: 8,
                            paddingRight: 0,
                            minWidth: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {shortShipmentAnswers[POItem.internalId] || ""}
                        </Stack>
                      )}
                  </Stack>
                );
              })}
              {/* Total Quantity - positioned outside the table */}
              {selectedPOItems.length > 0 && (
                <Stack
                  style={{
                    marginTop: 10,
                    fontSize: 11,
                    fontFamily: "tmwCustomFont",
                    textTransform: "uppercase",
                    gap: 5,
                    flexDirection: "row",
                  }}
                >
                  <span>Total Quantity:</span>
                  {selectedPOItems.reduce((sum, item) => {
                    const qty = quantityValues[item.internalId];
                    return sum + (qty && !isNaN(Number(qty)) ? Number(qty) : 0);
                  }, 0)}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Stack
          style={{
            width: "95%",
            alignItems: "flex-start",
          }}
        >
          <Stack
            sx={{
              width: { xs: "95%", md: "80%" },
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: "25px 50px 25px 25px",
              overflowX: "auto",
              overflowY: "hidden",
              border: "1px solid #E6E6E6",
            }}
          >
            <Stack
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                gap: 25,
              }}
            >
              {renderCurrencySelect()}
              {renderTotalUnitsSection()}
              {renderInputSelect(
                packingListFileData
                  ? "Packing List Document"
                  : "Upload Packing List Document",
                packingListFileName,
                setPackingListFileName,
                packingListFileData,
                setPackingListFileData,
                ["pdf", "xls", "xlsx", "doc", "docx"],
              )}
              {renderInputSelect(
                invoiceFileData ? "Invoice" : "Upload Invoice",
                invoiceFileName,
                setInvoiceFileName,
                invoiceFileData,
                setInvoiceFileData,
                ["pdf", "xls", "xlsx", "doc", "docx"],
              )}
            </Stack>
          </Stack>
        </Stack>
        {renderPickupDetailsSection()}
      </Stack>
    );
  };

  const renderTotalUnitsSection = () => {
    const calculatedTotalUnits = selectedPOItems.reduce((sum, item) => {
      const qty = quantityValues[item.internalId];
      return sum + (qty && !isNaN(Number(qty)) ? Number(qty) : 0);
    }, 0);

    return (
      <Stack
        style={{
          padding: "10px 0",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <CustomTypography
          message="Total units (qty):"
          style={{ fontSize: 11, width: "200px" }}
        />
        <CustomTextField
          style={{ width: "200px" }}
          value={totalUnits}
          type="number"
          handleChange={(e) => {
            const val = e.target.value;
            if (isNaN(Number(val))) {
              setTotalUnits("");
              setTotalUnitsError("");
              return;
            }
            if (!/^\d+$/.test(val)) return;
            setTotalUnits(val);
            const num = Number(val);
            if (num !== calculatedTotalUnits) {
              setTotalUnitsError("Value must equal the Total Quantity.");
            } else {
              setTotalUnitsError("");
            }
          }}
          error={!!totalUnitsError}
          helperText={totalUnitsError}
        />
      </Stack>
    );
  };

  const renderImportZedonkButton = () => {
    const handleButtonClick = () => {
      zedonkFileInputRef.current?.click();
    };

    const convertKeysToCamelCase = (data) => {
      return data.map((row) => {
        const newRow = {};
        Object.keys(row).forEach((key) => {
          newRow[camelCase(key)] = row[key];
        });
        return newRow;
      });
    };

    const handleFileChange = async (event) => {
      setImportZedonkParsing(true);

      const file = event.target.files[0];
      if (!file) return;

      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 0,
          defval: "",
        });

        const parsedData = convertKeysToCamelCase(jsonData);
        const filteredData = parsedData.map((row) => ({
          eanCode: row.barcode,
          quantity: row.inTransitQty,
        }));

        const quantityMap = new Map(
          filteredData.map((item) => [item.eanCode, item.quantity]),
        );

        const matchingObjects = POItems.filter((item) => {
          const hasEanCode = quantityMap.has(item.eanCode);
          const isNotCancelled = item.isCancelled === false;

          // For Invested brands, only check if not cancelled
          if (purchaseOrder?.brandType === "Invested") {
            return hasEanCode && isNotCancelled;
          }
          // For non-Invested brands, keep original logic
          return hasEanCode && item.remainingQuantity > 0 && isNotCancelled;
        }).map((item) => {
          const requestedQty = quantityMap.get(item.eanCode);

          // For Invested brands, don't cap the quantity
          const cappedQty =
            purchaseOrder?.brandType === "Invested"
              ? requestedQty
              : Math.min(requestedQty, item.remainingQuantity);

          return {
            ...item,
            quantity: cappedQty,
          };
        });

        // Select the imported items
        setSelectedPOItems(matchingObjects);

        setCurrentModalSection("remaining_fields");
        setSelectPurchaseOrderItemsModalVisible(false);

        toast(
          <div>
            <Stack>Zedonk file imported successfully</Stack>
          </div>,
          {
            duration: 3000,
            style: {
              fontFamily: "tmwCustomFont",
              letterSpacing: 1,
              fontSize: 12,
              backgroundColor: "white",
              color: "black",
              border: "1px solid green",
            },
          },
        );

        setImportZedonkParsing(false);
      } catch (error) {
        console.error("Parsing or uploading failed:", error);

        toast(
          <div>
            <Stack>Zedonk file import error</Stack>
          </div>,
          {
            duration: 3000,
            style: {
              fontFamily: "tmwCustomFont",
              letterSpacing: 1,
              fontSize: 12,
              backgroundColor: "white",
              color: "black",
              border: "1px solid red",
            },
          },
        );

        setImportZedonkParsing(false);
      }
    };

    return (
      purchaseOrder?.brandType === "Invested" && (
        <Stack style={{ marginRight: 25 }}>
          <CustomButton
            style={{ width: "200px" }}
            label={
              <Stack
                style={{
                  flexDirection: "row",
                  gap: 5,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PlaylistAdd />
                Import Zedonk
              </Stack>
            }
            onClick={handleButtonClick}
          />
          <input
            type="file"
            ref={zedonkFileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </Stack>
      )
    );
  };

  const inputRef = useRef(null);

  const renderImportExcelButton = () => {
    const handleImport = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const importedMap = new Map();
        const cappedItems = [];
        jsonData.forEach((row) => {
          const id = row["Internal ID"];
          if (id !== undefined) {
            importedMap.set(Number(id), row);
          }
        });

        setQuantityValues((prev) => {
          const updated = { ...prev };
          filteredPOItems.forEach((item) => {
            if (importedMap.has(Number(item.internalId))) {
              const row = importedMap.get(Number(item.internalId));
              let newQty = Math.round(Number(row["Ready To Ship"]));

              const foundExistingPOItem = Array.isArray(
                inboundRequestsItemsQuantityOfPO,
              )
                ? inboundRequestsItemsQuantityOfPO.find(
                    (po) => po.internalId === item.internalId,
                  )
                : undefined;

              const maxQty = foundExistingPOItem
                ? item.remainingQuantity + foundExistingPOItem.qty
                : item.remainingQuantity;

              // For Invested brands, allow any quantity (including 0) and don't cap at maxQty
              // For non-Invested brands (Distribution), only allow positive quantities and cap at maxQty
              if (purchaseOrder?.brandType === "Invested") {
                if (Number(newQty) >= 0) {
                  updated[item.internalId] = Number(newQty);
                }
              } else {
                if (Number(newQty) > 0) {
                  if (newQty > maxQty) {
                    cappedItems.push(item);
                  }
                  updated[item.internalId] = Math.min(newQty, maxQty);
                }
              }
            }
          });
          return updated;
        });

        // Select items where Selected Quantity > 0
        const importedSelectedItems = filteredPOItems.filter((item) => {
          if (importedMap.has(Number(item.internalId))) {
            const row = importedMap.get(Number(item.internalId));
            const newQty = Math.round(Number(row["Ready To Ship"]));

            // For distribution brands, don't select items with 0 quantity left even if Excel has selection > 0
            // For invested brands, allow selection regardless of remaining quantity
            if (purchaseOrder?.brandType === "Invested") {
              return Number(newQty) > 0;
            } else {
              return Number(newQty) > 0 && item.remainingQuantity > 0;
            }
          }
          return false;
        });
        setSelectedPOItems(importedSelectedItems);

        // Invested only: import Short Shipment (Yes/No) from Excel
        if (purchaseOrder?.brandType === "Invested") {
          setShortShipmentAnswers((prev) => {
            const next = { ...prev };
            filteredPOItems.forEach((item) => {
              if (importedMap.has(Number(item.internalId))) {
                const row = importedMap.get(Number(item.internalId));
                const raw =
                  row["Short Shipment (Yes/No)"] ??
                  row["Short Shipment"] ??
                  row["Short_Shipment"] ??
                  "";
                const normalized =
                  String(raw).trim().toLowerCase() === "yes"
                    ? "Yes"
                    : String(raw).trim().toLowerCase() === "no"
                      ? "No"
                      : "";
                if (normalized) {
                  next[item.internalId] = normalized;
                }
              }
            });
            return next;
          });
        }

        toast.success("Excel file imported successfully");
        if (cappedItems.length > 0) {
          toast(
            "Some Ready To Ship quantities exceeded the PO quantity and were capped to the PO value.",
          );
        }
      } catch (err) {
        toast.error("Failed to import Excel file");
      } finally {
        event.target.value = "";
      }
    };

    return (
      <CustomButton
        component="label"
        label={
          <Stack
            style={{
              width: "150px",
              flexDirection: "row",
              gap: 5,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FileUpload />
            Import Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={inputRef}
              style={{ display: "none" }}
              onChange={handleImport}
            />
          </Stack>
        }
      />
    );
  };

  // --- PATCH EXPORT TO FILTER OUT CANCELLED/NO REMAINING ITEMS ---
  const renderExportExcelButton = () => {
    const handleExport = () => {
      const exportData = filteredPOItems
        .filter((POItem) => {
          const isNotCancelled = !POItem.isCancelled;
          const isSelected = selectedPOItems.some(
            (item) => item.internalId === POItem.internalId,
          );

          // For Invested brands, only filter out cancelled items
          if (purchaseOrder?.brandType === "Invested") {
            return isNotCancelled;
          }
          // For non-Invested brands, keep original logic
          return (
            isNotCancelled && !(POItem.remainingQuantity === 0 && !isSelected)
          );
        })
        .sort((a, b) => a.internalId - b.internalId)
        .map((POItem) => {
          const foundExistingPOItem = Array.isArray(
            inboundRequestsItemsQuantityOfPO,
          )
            ? inboundRequestsItemsQuantityOfPO.find(
                (item) => item.internalId === POItem.internalId,
              )
            : undefined;
          const isChecked = selectedPOItems.some(
            (item) => item.internalId === POItem.internalId,
          );
          // Mirror editInboundRequest export behavior:
          // - If checked, use the exact input value (blank if unset/invalid)
          // - If not checked, use the remaining quantity
          const exportedQtyValue = quantityValues[POItem.internalId];
          const exportedQty = isChecked
            ? exportedQtyValue === undefined ||
              exportedQtyValue === "" ||
              Number.isNaN(Number(exportedQtyValue))
              ? ""
              : exportedQtyValue
            : POItem.remainingQuantity;

          const baseExportData = {
            "Internal ID": POItem.internalId,
            "Item SKU": POItem.itemSku,
            "EAN Code": POItem.eanCode,
            "Unit Cost": POItem.rate,
            "Colour Description": POItem.colourDescription,
            Description: POItem.description,
            "Vendor Code": POItem.vendorCode,
            "PO Qty": POItem.qty,
            [purchaseOrder?.brandType === "Invested"
              ? "Pending"
              : "Pending Qty"]: POItem.remainingQuantity,
          };

          baseExportData["Ready To Ship"] = exportedQty;

          // Only include additional tolerance columns for Invested brands (non-supplier views)
          if (showToleranceColumns) {
            const maxTolerance = Number(POItem.acceptedToleranceAmount ?? 0);
            baseExportData[
              purchaseOrder?.brandType === "Invested"
                ? "Tolerance"
                : "Max Tolerance"
            ] = maxTolerance;
          }

          if (showToleranceColumns) {
            // Calculate Extra Units (Ready To Ship - Pending Qty, minimum 0)
            const readyToShip = Number(exportedQty);
            const pendingQty = Number(POItem.remainingQuantity);
            const extraUnits = Math.max(0, readyToShip - pendingQty);
            baseExportData[
              purchaseOrder?.brandType === "Invested" ? "Extra" : "Extra Units"
            ] = extraUnits;
          }

          // Only include additional tolerance columns for Invested brands (non-supplier views)
          if (showToleranceColumns) {
            const baseDiff = Number(POItem.difference ?? 0);
            const currentNew = Number(
              quantityValues[POItem.internalId] ?? POItem.remainingQuantity,
            );
            const adjustedDiff = Math.max(0, -(baseDiff + currentNew));
            const signedAdjustedDiff = `${
              adjustedDiff > 0 ? "+" : ""
            }${adjustedDiff}`;

            baseExportData[
              purchaseOrder?.brandType === "Invested"
                ? "Remaining"
                : "Remaining Qty"
            ] = signedAdjustedDiff;

            // Calculate Discrepancy
            const permittedNumeric = Number(
              POItem.acceptedToleranceAmount ?? 0,
            );
            const readyToShip = Number(exportedQty);
            const pendingQty = Number(POItem.remainingQuantity);

            let discrepancy = 0;
            if (readyToShip > pendingQty) {
              // Overshipment
              discrepancy = readyToShip - (pendingQty + permittedNumeric);
              if (discrepancy <= 0) discrepancy = 0;
            } else if (readyToShip < pendingQty) {
              // Undershipment
              discrepancy = readyToShip - (pendingQty - permittedNumeric);
              if (discrepancy >= 0) discrepancy = 0;
            }

            baseExportData["Discrepancy"] = discrepancy;
          }

          // Invested only: export Short Shipment (Yes/No) for import round-trip
          if (purchaseOrder?.brandType === "Invested") {
            baseExportData["Short Shipment (Yes/No)"] =
              shortShipmentAnswers[POItem.internalId] || "";
          }

          return baseExportData;
        });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "PO Items");

      XLSX.writeFile(workbook, "inbound-request-po-items.xlsx");
    };

    return (
      <CustomButton
        label={
          <Stack
            style={{
              width: "150px",
              flexDirection: "row",
              gap: 5,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GetApp />
            Export Excel
          </Stack>
        }
        onClick={handleExport}
      />
    );
  };

  const renderChoosePOItemsSection = () => {
    const handleQuantityChange = (name, maxValue) => (event) => {
      const value = event.target.value;

      // Allow empty input (so users can delete & retype)
      if (value === "") {
        setQuantityValues((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      // Reject non-integer input while typing
      if (!/^\d+$/.test(value)) {
        return;
      }

      const num = Number(value);

      // For Invested brands, allow any positive integer
      // For non-Invested brands, allow only integers within range
      if (purchaseOrder?.brandType === "Invested") {
        if (num >= 1) {
          setQuantityValues((prev) => ({ ...prev, [name]: num }));
        }
      } else {
        if (num >= 1 && num <= maxValue) {
          setQuantityValues((prev) => ({ ...prev, [name]: num }));
        }
      }
    };

    const handleBlur = (name, maxValue) => () => {
      let num = Number(quantityValues[name]);

      // Normalize to an integer and clamp
      if (!Number.isInteger(num)) num = Math.round(num);
      if (isNaN(num) || num < 1) num = 1;

      // For Invested brands, don't cap the maximum
      // For non-Invested brands, cap at maxValue
      if (purchaseOrder?.brandType !== "Invested" && num > maxValue) {
        num = maxValue;
      }

      setQuantityValues((prev) => ({ ...prev, [name]: num }));
    };

    const renderSearchPOItem = () => {
      return (
        <Stack>
          <CustomTextField
            label="Search"
            value={searchPOItem || ""}
            handleChange={(e) => setSearchPOItem(e.target.value)}
            style={{ width: "200px" }}
            inputProps={{
              InputProps: {
                endAdornment: searchPOItem && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchPOItem("")}>
                      <ClearIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      );
    };

    const renderQuantitySection = () => {
      const totalQuantities = POItems.reduce(
        (sum, item) => sum + (item.qty || 0),
        0,
      );

      return (
        <Stack
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            margin: "0 15px",
          }}
        >
          <CustomTypography message="TOTAL QUANTITY INCLUDED IN INBOUND REQUESTS:" />
          <Stack style={{ fontFamily: "tmwCustomFont", fontSize: 14 }}>
            {inboundRequestsItemsQuantityOfPO} / {totalQuantities}
          </Stack>
        </Stack>
      );
    };

    const renderCheckAllItems = () => {
      const message = searchPOItem
        ? "Select all items (With filters)"
        : "Select all items";

      return (
        <CustomFormControl
          control={
            <Checkbox
              style={{
                color: "black",
              }}
            />
          }
          label={<CustomTypography message={message} />}
          checked={selectAllItemsChecked}
          onChange={() => setSelectAllItemsChecked(!selectAllItemsChecked)}
        />
      );
    };

    const renderSearchCount = () => {
      return (
        !!searchPOItem && (
          <Stack
            style={{
              alignItems: "flex-end",
              position: "relative",
              marginBottom: -5,
            }}
          >
            <CustomTypography
              style={{ marginTop: 10, marginRight: 10, position: "absolute" }}
              message={`Found Items: ${filteredPOItems.length}`}
            />
          </Stack>
        )
      );
    };

    return (
      <>
        <Stack
          style={{
            width: "100%",
            alignTtems: "center",
            justifyContent: "center",
            flexDirection: "row",
          }}
        >
          <Stack style={{ width: "100%", alignItems: "center" }}>
            <CustomTypography message="Select The Purchase Order Items" title />
          </Stack>
        </Stack>
        <Stack
          style={{
            gap: 10,
            padding: "25px",
            margin: "15px 0 25px 0",
            width: "100%",
            border: "1px solid #E6E6E6",
          }}
        >
          <Stack
            style={{
              flexDirection: "row",
              width: "100%",
              justifyContent: "space-between",
              marginBottom: 15,
            }}
          >
            {renderCheckAllItems()}
            <Stack
              style={{
                flexDirection: "row",
                gap: 15,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {renderExportExcelButton()}
              {renderImportExcelButton()}
              {userGroup !== groupTypes.supplier && renderImportZedonkButton()}
              {renderSearchPOItem()}
            </Stack>
          </Stack>
          <Stack
            style={{
              gap: 5,
              fontFamily: "tmwCustomFont",
              border: "1px solid #E6E6E6",
              marginBottom: 25,
            }}
          >
            {/* Column Labels */}
            {(() => {
              const isInvested = purchaseOrder?.brandType === "Invested";
              const showToleranceCols =
                isInvested && userGroup !== groupTypes.supplier;
              const gridTemplateColumns = isInvested
                ? showToleranceCols
                  ? "40px 1fr 1.5fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr"
                  : "40px 1fr 1.5fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1fr"
                : "40px 1fr 2fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1.5fr 1fr";
              return (
                <Stack
                  style={{
                    display: "grid",
                    gridTemplateColumns,
                    alignItems: "flex-start",
                    gap: 10,
                    fontWeight: "bold",
                    padding: 10,
                    borderBottom: "1px solid #E6E6E6",
                    userSelect: "none",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: 8,
                  }}
                >
                  <div />
                  <Stack
                    style={{
                      textAlign: "center",
                      paddingLeft: 0,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    ID
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "center",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Item SKU
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "center",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    EAN Code
                  </Stack>
                  {/* numeric */}
                  <Stack
                    style={{
                      textAlign: "right",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Unit Cost
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "center",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Colour Description
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "center",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Description
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "center",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Vendor Code
                  </Stack>

                  {/* numeric from here on */}
                  <Stack
                    style={{
                      textAlign: "right",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    PO Qty
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "right",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {purchaseOrder?.brandType === "Invested"
                      ? "Pending"
                      : "Pending Qty"}
                  </Stack>
                  <Stack
                    style={{
                      textAlign: "right",
                      paddingLeft: 8,
                      paddingRight: 8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Ready To Ship
                  </Stack>
                  {showToleranceCols && (
                    <Stack
                      style={{
                        textAlign: "right",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {purchaseOrder?.brandType === "Invested"
                        ? "Tolerance"
                        : "Max Tolerance"}
                    </Stack>
                  )}
                  {showToleranceCols && (
                    <>
                      <Stack
                        style={{
                          textAlign: "right",
                          paddingLeft: 8,
                          paddingRight: 8,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {purchaseOrder?.brandType === "Invested"
                          ? "Extra"
                          : "Extra Units"}
                      </Stack>
                      <Stack
                        style={{
                          textAlign: "right",
                          paddingLeft: 8,
                          paddingRight: 8,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {purchaseOrder?.brandType === "Invested"
                          ? "Remaining"
                          : "Remaining Qty"}
                      </Stack>
                      {isInvested && (
                        <Stack
                          style={{
                            textAlign: "right",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          Discrepancy
                        </Stack>
                      )}
                    </>
                  )}
                  {isInvested && (
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Short Shipment (Yes/No)
                    </Stack>
                  )}
                </Stack>
              );
            })()}

            {/* Selectable Items */}
            <Stack style={{ maxHeight: "50vh", overflow: "auto" }}>
              {filteredPOItems.length === 0 ? (
                <Stack
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "50px 20px",
                    fontSize: 10,
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  No items found
                </Stack>
              ) : (
                filteredPOItems
                  ?.sort((a, b) => a.internalId - b.internalId)
                  .map((POItem, index) => {
                    const isChecked = selectedPOItems.some(
                      (item) => item.internalId === POItem.internalId,
                    );
                    const disabledItem =
                      purchaseOrder?.brandType === "Invested"
                        ? POItem.isCancelled
                        : POItem.remainingQuantity <= 0 || POItem.isCancelled;

                    const isInvested = purchaseOrder?.brandType === "Invested";

                    const showToleranceCols =
                      isInvested && userGroup !== groupTypes.supplier;

                    const gridTemplateColumns = isInvested
                      ? showToleranceCols
                        ? "40px 1fr 1.5fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr"
                        : "40px 1fr 1.5fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1fr"
                      : "40px 1fr 2fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1.5fr 1fr";

                    return (
                      <Stack
                        key={`POItem-${index}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns,
                          alignItems: "center",
                          gap: 10,
                          margin: "2px 0",
                          cursor: "pointer",
                          padding: "5px",
                          transition: "background-color 0.2s ease-in-out",
                          userSelect: "none",
                          fontSize: 14,
                          minHeight: "50px",
                          background: POItem.isCancelled
                            ? "rgba(255, 237, 237)"
                            : "",
                        }}
                        sx={{
                          color: disabledItem ? "lightgrey" : "black",
                          "&:hover": !disabledItem && {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        <Checkbox
                          className="hover-checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (!disabledItem) {
                              setSelectedPOItems((prev) =>
                                isChecked
                                  ? prev.filter(
                                      (item) =>
                                        item.internalId !== POItem.internalId,
                                    )
                                  : [...prev, POItem],
                              );
                              if (isChecked) {
                                setShortShipmentAnswers((prev) => {
                                  const next = { ...prev };
                                  delete next[POItem.internalId];
                                  return next;
                                });
                              }
                            }
                          }}
                          style={{
                            color: disabledItem ? "lightgrey" : "black",
                            borderRadius: 0,
                            padding: 0,
                            marginTop: -5,
                          }}
                        />
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "center",
                            paddingLeft: 0,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.internalId}
                        </Stack>
                        <Stack
                          style={{
                            flex: 2,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.itemSku}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1.5,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.eanCode}
                        </Stack>
                        {/* Unit cost */}
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.rate}
                        </Stack>
                        <Stack
                          style={{
                            flex: 2,
                            textAlign: "left",
                            paddingLeft: 32,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.colourDescription}
                        </Stack>
                        <Stack
                          style={{
                            flex: 2,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.description}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.vendorCode || "N/A"}
                        </Stack>
                        {/* PO Qty */}
                        <Stack
                          style={{
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.qty}
                        </Stack>
                        {/* Pending Qty */}
                        <Stack
                          style={{
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.remainingQuantity}
                        </Stack>
                        {/* Ready To Ship */}
                        <Stack
                          style={{
                            flex: 0.5,
                            alignItems: "flex-end",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          <CustomTextField
                            id={`quantity-${index}`}
                            disabled={
                              POItem.isCancelled ||
                              disabledItem ||
                              isNaN(quantityValues[POItem.internalId])
                            }
                            inputProps={{
                              min: 0,
                              max:
                                purchaseOrder?.brandType === "Invested"
                                  ? POItem.qty +
                                    (POItem.acceptedToleranceAmount || 0)
                                  : POItem.remainingQuantity,
                              step: 1,
                            }}
                            handleChange={handleQuantityChange(
                              POItem.internalId,
                              purchaseOrder?.brandType === "Invested"
                                ? POItem.qty +
                                    (POItem.acceptedToleranceAmount || 0)
                                : POItem.remainingQuantity,
                            )}
                            style={{
                              alignItems: "flex-start",
                              margin: 0,
                              padding: 0,
                              marginTop: -5,
                              paddingTop: 0,
                            }}
                            textFieldStyle={{
                              width: "50px",
                              margin: 0,
                              padding: 0,
                              marginTop: -5,
                              paddingTop: 0,
                            }}
                            onBlur={handleBlur(
                              POItem.internalId,
                              purchaseOrder?.brandType === "Invested"
                                ? POItem.qty +
                                    (POItem.acceptedToleranceAmount || 0)
                                : POItem.remainingQuantity,
                            )}
                            value={quantityValues[POItem.internalId] ?? ""}
                            type="number"
                          />
                        </Stack>
                        {showToleranceColumns && (
                          <>
                            {/* Max Tolerance */}
                            <Stack
                              style={{
                                flex: 1,
                                paddingLeft: 8,
                                paddingRight: 8,
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                textAlign: "right",
                              }}
                            >
                              {(() => {
                                const permittedNumeric = Number(
                                  POItem.acceptedToleranceAmount ?? 0,
                                );
                                return <span>{permittedNumeric}</span>;
                              })()}
                            </Stack>
                          </>
                        )}
                        {showToleranceColumns && (
                          <>
                            {/* Extra Units */}
                            <Stack
                              style={{
                                flex: 1,
                                paddingLeft: 8,
                                paddingRight: 8,
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                textAlign: "right",
                              }}
                            >
                              {(() => {
                                const readyToShip = Number(
                                  quantityValues[POItem.internalId] ??
                                    POItem.remainingQuantity,
                                );
                                const pendingQty = Number(
                                  POItem.remainingQuantity,
                                );
                                const extraUnits = Math.max(
                                  0,
                                  readyToShip - pendingQty,
                                );
                                return <span>{extraUnits}</span>;
                              })()}
                            </Stack>
                            {/* Remaining Qty */}
                            <Stack
                              style={{
                                flex: 1,
                                paddingLeft: 8,
                                paddingRight: 8,
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                textAlign: "right",
                              }}
                            >
                              {(() => {
                                const baseDiff = Number(POItem.difference ?? 0);
                                const currentNew = Number(
                                  quantityValues[POItem.internalId] ??
                                    POItem.remainingQuantity,
                                );
                                const adjustedDiff = Math.max(
                                  0,
                                  -(baseDiff + currentNew),
                                );
                                return <span>{adjustedDiff}</span>;
                              })()}
                            </Stack>
                            {/* Discrepancy - only for Invested */}
                            {purchaseOrder?.brandType === "Invested" && (
                              <Stack
                                style={{
                                  flex: 1,
                                  paddingLeft: 8,
                                  paddingRight: 8,
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  textAlign: "right",
                                }}
                              >
                                {(() => {
                                  const permittedNumeric = Number(
                                    POItem.acceptedToleranceAmount ?? 0,
                                  );
                                  const readyToShip = Number(
                                    quantityValues[POItem.internalId] ??
                                      POItem.remainingQuantity,
                                  );
                                  const pendingQty = Number(
                                    POItem.remainingQuantity,
                                  );

                                  // Calculate Discrepancy
                                  let discrepancy = 0;
                                  if (readyToShip > pendingQty) {
                                    // Overshipment
                                    discrepancy =
                                      readyToShip -
                                      (pendingQty + permittedNumeric);
                                    if (discrepancy <= 0) discrepancy = 0;
                                  } else if (readyToShip < pendingQty) {
                                    // Undershipment
                                    discrepancy =
                                      readyToShip -
                                      (pendingQty - permittedNumeric);
                                    if (discrepancy >= 0) discrepancy = 0;
                                  }

                                  return (
                                    <span
                                      style={{
                                        color:
                                          discrepancy !== 0 ? "red" : "inherit",
                                      }}
                                    >
                                      {discrepancy}
                                    </span>
                                  );
                                })()}
                              </Stack>
                            )}
                          </>
                        )}
                        {purchaseOrder?.brandType === "Invested" && (
                          <Stack
                            style={{
                              flex: 1.5,
                              alignItems: "center",
                            }}
                          >
                            {(() => {
                              const currentNew = Number(
                                quantityValues[POItem.internalId] ??
                                  POItem.remainingQuantity,
                              );
                              const qtyLeft = Number(POItem.remainingQuantity);

                              const isRowSelected = selectedPOItems.some(
                                (it) => it.internalId === POItem.internalId,
                              );
                              const showShortShipment =
                                isRowSelected && currentNew < qtyLeft;

                              return (
                                <Stack
                                  style={{
                                    flexDirection: "row",
                                    gap: 5,
                                    flex: 1,
                                  }}
                                >
                                  {showShortShipment && !POItem.isCancelled && (
                                    <select
                                      value={
                                        shortShipmentAnswers[
                                          POItem.internalId
                                        ] ?? ""
                                      }
                                      onChange={(e) =>
                                        setShortShipmentAnswers((prev) => ({
                                          ...prev,
                                          [POItem.internalId]: e.target.value,
                                        }))
                                      }
                                      style={{
                                        border: "1px solid #E6E6E6",
                                        fontFamily:
                                          "tmwCustomFont, Arial, sans-serif",
                                        fontSize: 12,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        backgroundColor: "white",
                                        minWidth: "80px",
                                        font: "inherit",
                                      }}
                                      disabled={
                                        !(
                                          showShortShipment &&
                                          !POItem.isCancelled
                                        )
                                      }
                                    >
                                      <option value="" disabled>
                                        Select
                                      </option>
                                      <option value="Yes">Yes</option>
                                      <option value="No">No</option>
                                    </select>
                                  )}
                                </Stack>
                              );
                            })()}
                          </Stack>
                        )}
                      </Stack>
                    );
                  })
              )}
            </Stack>
            {renderSearchCount()}
          </Stack>
          <Stack
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 10,
              marginTop: "15px",
            }}
          >
            {renderQuantitySection()}
            <Stack style={{ flexDirection: "row", gap: 15 }}>
              {!cancelButtonClicked && (
                <CustomButton
                  label="Cancel"
                  style={{ width: "100px" }}
                  onClick={() =>
                    (window.location.href =
                      inboundType === "requests"
                        ? paths.dashboard.inbound.requests.index
                        : paths.dashboard.inbound.asn.index)
                  }
                />
              )}
              <CustomButton
                disabled={(() => {
                  if (!selectedPOItems.length) return true;
                  // Zero quantity or empty for selected items
                  const hasZeroQtySelected = selectedPOItems.some((item) => {
                    const raw = quantityValues[item.internalId];
                    if (raw === "") return true;
                    const num = Number(raw);
                    return !Number.isFinite(num) || num === 0;
                  });

                  // For Invested brands, ensure short shipment answers provided when qty < remaining
                  const hasUnansweredShortForSelected =
                    purchaseOrder?.brandType === "Invested"
                      ? selectedPOItems.some((poItem) => {
                          const foundPOItem = POItems.find(
                            (po) => po.internalId === poItem.internalId,
                          );
                          if (!foundPOItem) return false;
                          const qtyLeft = Number(foundPOItem.remainingQuantity);
                          const hasExplicitQty =
                            Object.prototype.hasOwnProperty.call(
                              quantityValues,
                              poItem.internalId,
                            ) &&
                            quantityValues[poItem.internalId] !== "" &&
                            !isNaN(Number(quantityValues[poItem.internalId]));
                          const currentNew = hasExplicitQty
                            ? Number(quantityValues[poItem.internalId])
                            : qtyLeft;
                          const isShort =
                            currentNew < qtyLeft && !poItem.isCancelled;
                          if (!isShort) return false;
                          const answer =
                            shortShipmentAnswers[poItem.internalId];
                          return (
                            !answer || (answer !== "Yes" && answer !== "No")
                          );
                        })
                      : false;

                  return hasZeroQtySelected || hasUnansweredShortForSelected;
                })()}
                label="Next"
                style={{ width: "100px" }}
                onClick={() => {
                  setCurrentModalSection("remaining_fields");
                  setSelectPurchaseOrderItemsModalVisible(false);
                  setCancelButtonClicked(true);
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </>
    );
  };

  const renderSelectPurchaseOrderItemsSection = () => {
    return (
      <Stack
        style={{
          width: "95vw",
          alignItems: "center",
          alignSelf: "center",
          position: "relative",
        }}
      >
        {currentModalSection === "po_items" &&
          (POItemsLoading ? (
            <CircularProgress
              size={35}
              style={{
                color: "black",
                alignSelf: "center",
              }}
            />
          ) : (
            renderChoosePOItemsSection()
          ))}
      </Stack>
    );
  };

  const renderBackButton = () => {
    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "0 10px",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            cursor: "pointer",
            padding: 5,
          }}
          sx={{
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() =>
            (window.location.href =
              inboundType === "requests"
                ? paths.dashboard.inbound.requests.index
                : paths.dashboard.inbound.asn.index)
          }
        >
          <KeyboardBackspace />
          <CustomTypography message="Inbound Requests" />
        </Stack>
      </Stack>
    );
  };

  const renderPOInfo = () => {
    if (!purchaseOrder || POVerificationLoading) return null;

    const align = mdUp ? "horizontal" : "vertical";

    const createdDate = purchaseOrder.createdAt
      ? moment(purchaseOrder.createdAt).format("DD/MM/YYYY")
      : "No Date";
    const openDeliveryWindow = purchaseOrder.openDeliveryWindow
      ? moment(purchaseOrder.openDeliveryWindow).format("DD/MM/YYYY")
      : "No Date";
    const closeDeliveryWindow = purchaseOrder.closeDeliveryWindow
      ? moment(purchaseOrder.closeDeliveryWindow).format("DD/MM/YYYY")
      : "No Date";

    const statusCircleColor = "#F5911E"; // New IR defaults to Draft-like context

    const ExpandMore = styled((props) => {
      const { expand, ...other } = props;
      return <IconButton {...other} />;
    })(({ theme, expand }) => ({
      transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
      marginLeft: "auto",
      marginBottom: "auto",
      transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
      }),
    }));

    const PropertyListColumn = ({ label, value, align }) => (
      <div style={{ flex: "1", textAlign: align }}>
        <PropertyList>
          <PropertyListItem label={label} value={value} />
        </PropertyList>
      </div>
    );

    return (
      <Card style={{ boxShadow: "none", marginBottom: 10 }}>
        <Stack
          style={{
            marginLeft: "15px",
            display: "flex",
            flexDirection: "row",
            gap: "15px",
            justifyContent: "space-between",
            padding: "12px 15px 12px 25px",
          }}
        >
          <Stack style={{ flexDirection: "row", gap: "15px" }}>
            <Stack style={{ justifyContent: "center" }}>
              <div
                style={{
                  margin: "5px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  border: `1.5px solid ${statusCircleColor}`,
                  background: statusCircleColor,
                }}
              />
            </Stack>
            <Stack
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
              }}
            >
              <Stack
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 23,
                  fontWeight: 500,
                }}
              >
                {purchaseOrder.documentNumber ||
                  purchaseOrder.poNumber ||
                  purchaseOrder.number}
              </Stack>
              <Stack
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {`Inbound Request Status: Draft`}
                {` | Brand: ${
                  purchaseOrder.brandName ||
                  purchaseOrder.brand?.name ||
                  purchaseOrder.brand?.brandName ||
                  purchaseOrder.brand ||
                  "N/A"
                }`}
                {` | Season: ${
                  purchaseOrder.season || purchaseOrder.seasonName || "N/A"
                }`}
                {` | Incoterm: ${purchaseOrder.incoterm || "N/A"}`}
              </Stack>
              <Stack
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {`NS Transaction Status: ${purchaseOrder.nsTransactionStatus}`}
              </Stack>
            </Stack>
          </Stack>
          <div>
            <ExpandMore
              expand={poSummaryExpanded}
              onClick={() => setPoSummaryExpanded(!poSummaryExpanded)}
              aria-expanded={poSummaryExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </div>
        </Stack>
        <Collapse in={poSummaryExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Created Date:"
                value={createdDate}
                align={align}
              />
              <PropertyListColumn
                label="Document Number:"
                value={
                  purchaseOrder.documentNumber ||
                  purchaseOrder.poNumber ||
                  purchaseOrder.number
                }
                align={align}
              />
              <PropertyListColumn
                label="Supplier:"
                value={purchaseOrder.supplier}
                align={align}
              />
              <PropertyListColumn
                label="Memo:"
                value={purchaseOrder.memo}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Subsidiary:"
                value={purchaseOrder.subsidiary}
                align={align}
              />
              <PropertyListColumn
                label="Location:"
                value={purchaseOrder.location}
                align={align}
              />
              <PropertyListColumn
                label="Brand:"
                value={
                  purchaseOrder.brandName ||
                  purchaseOrder.brand?.name ||
                  purchaseOrder.brand?.brandName ||
                  purchaseOrder.brand
                }
                align={align}
              />
              <PropertyListColumn
                label="Season:"
                value={purchaseOrder.season || purchaseOrder.seasonName}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Currency:"
                value={purchaseOrder.currency}
                align={align}
              />
              <PropertyListColumn
                label="Total Amount:"
                value={purchaseOrder.amountForeignCurrency?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Total Open Lines:"
                value={purchaseOrder.totalOpenLines?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Total Closed Lines:"
                value={purchaseOrder.totalClosedLines?.toLocaleString?.()}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Total Open Quantity:"
                value={purchaseOrder.totalOpenQty?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Total Closed Quantity:"
                value={purchaseOrder.totalClosedQty?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Quantity Received:"
                value={purchaseOrder.qtyReceived?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Quantity Billed:"
                value={purchaseOrder.qtyBilled?.toLocaleString?.()}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Incoterm:"
                value={purchaseOrder.incoterm || purchaseOrder.shippingTerms}
                align={align}
              />
              <PropertyListColumn
                label="Open Delivery Window:"
                value={openDeliveryWindow}
                align={align}
              />
              <PropertyListColumn
                label="Close Delivery Window:"
                value={closeDeliveryWindow}
                align={align}
              />
              <PropertyListColumn
                label="Netsuite Transaction Status:"
                value={purchaseOrder.nsTransactionStatus}
                align={align}
              />
            </div>
            <Divider />
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  const renderPickupDateAutoselect = () => {
    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
          }}
        >
          Pick-up Date
        </span>
        <MobileDatePicker
          inputFormat="dd/MM/yyyy"
          value={pickupDate ?? undefined}
          onChange={(date) => {
            setPickupDate(date);
          }}
          renderInput={(inputProps) => {
            return (
              <>
                <CustomTextField
                  style={{ width: "300px" }}
                  inputProps={{
                    value: inputProps.inputProps.value,
                    onClick: inputProps.inputProps.onClick,
                    endadorment: (
                      <InputAdornment position="end">
                        <img
                          src={CustomCalendarIcon}
                          style={{ width: "15px", borderRadius: "3px" }}
                          alt="calendar-icon"
                        />
                      </InputAdornment>
                    ),
                  }}
                  calendar
                />
              </>
            );
          }}
        />
      </Stack>
    );
  };

  const renderPickupTimeAutoselect = () => {
    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
          }}
        >
          Pick-up Time
        </span>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={pickupTime ?? undefined}
            onChange={() => {}}
            onAccept={(time) => {
              setPickupTime(time);
            }}
            ampm={false}
            minutesStep={15}
            views={["hours", "minutes"]}
            renderInput={(inputProps) => (
              <CustomTextField
                style={{ width: "300px" }}
                disabled
                inputProps={inputProps}
                onKeyDown={(e) => e.preventDefault()}
              />
            )}
          />
        </LocalizationProvider>
      </Stack>
    );
  };

  const renderNumberOfBoxes = () => {
    return (
      <Stack>
        <CustomTextField
          label="Number of boxes"
          type="number"
          style={{ width: "300px" }}
          value={numberOfBoxes || 0}
          handleChange={(e) =>
            e.target.value >= 0 && setNumberOfBoxes(e.target.value)
          }
        />
      </Stack>
    );
  };

  const renderInvoiceNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Invoice number:"
          style={{ fontSize: 12, width: "200px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={invoiceNumber || ""}
          handleChange={(e) => setInvoiceNumber(e.target.value)}
          error={!invoiceNumber}
          helperText={!invoiceNumber && "Invoice number is required."}
        />
      </Stack>
    );
  };

  const renderCarrierName = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTextField
          label="Carrier Name"
          style={{ width: "100%" }}
          value={carrierName || ""}
          handleChange={(e) => setCarrierName(e.target.value)}
        />
      </Stack>
    );
  };

  const renderDeliveryAddress = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Delivery Address (Tomorrow warehouse location):"
          style={{ fontSize: 12, width: "400px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={deliveryAddress || ""}
          handleChange={(e) => setDeliveryAddress(e.target.value)}
        />
      </Stack>
    );
  };

  const renderDeliveryNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Delivery number:"
          style={{ fontSize: 12, width: "220px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={deliveryNumber || ""}
          handleChange={(e) => setDeliveryNumber(e.target.value)}
        />
      </Stack>
    );
  };

  const renderTotalNumberOfCartons = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Total Number of Cartons:"
          style={{ fontSize: 12 }}
        />
        <CustomTextField
          style={{ width: "250px" }}
          value={totalNumberOfCartons}
          type="number"
          handleChange={(e) => setTotalNumberOfCartons(e.target.value)}
          error={
            totalNumberOfCartons === null ||
            totalNumberOfCartons === undefined ||
            totalNumberOfCartons === "" ||
            isNaN(Number(totalNumberOfCartons)) ||
            Number(totalNumberOfCartons) < 0
          }
          helperText={
            (totalNumberOfCartons === null ||
              totalNumberOfCartons === undefined ||
              totalNumberOfCartons === "" ||
              isNaN(Number(totalNumberOfCartons)) ||
              Number(totalNumberOfCartons) < 0) &&
            "Total number of cartons is required."
          }
        />
      </Stack>
    );
  };

  const renderTotalNumberOfPallets = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Total Number of Pallets:"
          style={{ fontSize: 12 }}
        />
        <CustomTextField
          style={{ width: "250px" }}
          value={totalNumberOfPallets || ""}
          type="number"
          handleChange={(e) => setTotalNumberOfPallets(e.target.value)}
          error={
            totalNumberOfPallets === null ||
            totalNumberOfPallets === undefined ||
            totalNumberOfPallets === "" ||
            isNaN(Number(totalNumberOfPallets)) ||
            Number(totalNumberOfPallets) < 0
          }
          helperText={
            (totalNumberOfPallets === null ||
              totalNumberOfPallets === undefined ||
              totalNumberOfPallets === "" ||
              isNaN(Number(totalNumberOfPallets)) ||
              Number(totalNumberOfPallets) < 0) &&
            "Total number of pallets is required."
          }
        />
      </Stack>
    );
  };

  const renderBoxDimensions = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Box Dimensions (CM or INCH):"
          style={{ fontSize: 12, width: "430px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={boxDimensions || ""}
          handleChange={(e) => setBoxDimensions(e.target.value)}
          multiline
          rows={2}
          error={!boxDimensions}
          helperText={!boxDimensions && "Box dimensions are required."}
        />
      </Stack>
    );
  };

  const renderPalletDimensions = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="If pallet, pallet dimensions:"
          style={{ fontSize: 12, width: "420px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={palletDimensions || ""}
          handleChange={(e) => setPalletDimensions(e.target.value)}
          multiline
          rows={2}
          error={!palletDimensions}
          helperText={!palletDimensions && "Pallet dimensions are required."}
        />
      </Stack>
    );
  };

  const renderTotalGrossWeight = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Total Gross Weight (KG/LB):"
          style={{ fontSize: 12, width: "230px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={totalGrossWeight || ""}
          handleChange={(e) => totalGrossWeightValidation(e.target.value)}
          type="text"
          inputProps={{ inputMode: "decimal" }}
          error={!!totalGrossWeightError || !totalGrossWeight}
          helperText={
            totalGrossWeightError ||
            (!totalGrossWeight && "Total gross weight is required.")
          }
        />
      </Stack>
    );
  };

  const renderTotalNetWeight = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Total Net Weight (KG/LB):"
          style={{ fontSize: 12, width: "230px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={totalNetWeight || ""}
          handleChange={(e) => totalNetWeightValidation(e.target.value)}
          type="text"
          inputProps={{ inputMode: "decimal" }}
          error={!!totalNetWeightError || !totalNetWeight}
          helperText={
            totalNetWeightError ||
            (!totalNetWeight && "Total net weight is required.")
          }
        />
      </Stack>
    );
  };

  // Shared autocomplete renderer (copied from editInboundRequest.js)
  const renderPickupAutocompleteField = (
    label,
    value,
    setValue,
    suggestions,
    disabled = false,
    fieldName = "",
  ) => {
    const fieldId = fieldName || label.toLowerCase().replace(/[^a-z0-9]/g, "");
    const fieldState = autocompleteStates[fieldId] || {
      showSuggestions: false,
      filteredSuggestions: [],
    };

    const updateFieldState = (updates) => {
      setAutocompleteStates((prev) => ({
        ...prev,
        [fieldId]: { ...fieldState, ...updates },
      }));
    };

    const handleInputChange = (e) => {
      const inputValue = e.target.value;
      setValue(inputValue);

      if (inputValue && suggestions && suggestions.length > 0) {
        const filtered = suggestions.filter((suggestion) =>
          suggestion.toLowerCase().includes(inputValue.toLowerCase()),
        );
        updateFieldState({
          filteredSuggestions: filtered,
          showSuggestions: filtered.length > 0,
        });
      } else if (suggestions && suggestions.length > 0) {
        updateFieldState({
          filteredSuggestions: suggestions,
          showSuggestions: true,
        });
      } else {
        updateFieldState({ showSuggestions: false });
      }
    };

    const handleSuggestionClick = (suggestion) => {
      setValue(suggestion);
      updateFieldState({ showSuggestions: false });
    };

    const handleInputClick = () => {
      if (suggestions && suggestions.length > 0) {
        updateFieldState({
          filteredSuggestions: suggestions,
          showSuggestions: true,
        });
      }
    };

    const handleInputBlur = () => {
      setTimeout(() => updateFieldState({ showSuggestions: false }), 200);
    };

    const pickupLocationFieldNames = [
      "pickupCompanyName",
      "pickupAddress",
      "pickupZipCode",
      "pickupCity",
      "pickupCountry",
    ];

    const isPickupLocationField = pickupLocationFieldNames.includes(fieldName);
    const isFieldRequired = isPickupLocationField || !isDDPIncoterm;

    return (
      <Stack
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          position: "relative",
        }}
      >
        <CustomTypography
          message={label}
          style={{ fontSize: 12, width: "210px" }}
        />
        <Stack style={{ width: "100%", position: "relative" }}>
          <div onClick={handleInputClick}>
            <CustomTextField
              style={{ width: "100%" }}
              value={value || ""}
              handleChange={handleInputChange}
              onBlur={handleInputBlur}
              error={!value && isFieldRequired}
              helperText={
                !value && isFieldRequired && "This field is required."
              }
              disabled={disabled}
            />
          </div>
          {fieldState.showSuggestions &&
            fieldState.filteredSuggestions.length > 0 && (
              <Paper
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #E6E6E6",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <List style={{ padding: 0 }}>
                  {fieldState.filteredSuggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: "8px 16px",
                        fontSize: "12px",
                        fontFamily: "tmwCustomFont",
                      }}
                    >
                      <ListItemText primary={suggestion} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
        </Stack>
      </Stack>
    );
  };

  const renderPickupCompanyName = () => {
    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupCompanyNames || [];
    return renderPickupAutocompleteField(
      "Pickup Company Name:",
      pickupCompanyName,
      setPickupCompanyName,
      suggestions,
      false,
      "pickupCompanyName",
    );
  };

  const renderPickupZipCode = () => {
    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupZipCodes || [];
    return renderPickupAutocompleteField(
      "Pickup Zip Code:",
      pickupZipCode,
      setPickupZipCode,
      suggestions,
      false,
      "pickupZipCode",
    );
  };

  const renderPickupAddress = () => {
    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupAddresses || [];
    return renderPickupAutocompleteField(
      "Pickup Address:",
      pickupAddress,
      setPickupAddress,
      suggestions,
      false,
      "pickupAddress",
    );
  };

  const renderPickupCity = () => {
    const suggestions = pickupAutocompleteData?.suggestions?.pickupCities || [];
    return renderPickupAutocompleteField(
      "Pickup City:",
      pickupCity,
      setPickupCity,
      suggestions,
      false,
      "pickupCity",
    );
  };

  const renderPickupCountry = () => {
    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupCountries || [];
    return renderPickupAutocompleteField(
      "Pickup Country:",
      pickupCountry,
      setPickupCountry,
      suggestions,
      false,
      "pickupCountry",
    );
  };

  const renderOpeningHours = () => {
    const suggestions = pickupAutocompleteData?.suggestions?.openingHours || [];
    return renderPickupAutocompleteField(
      "Opening Hours:",
      openingHours,
      setOpeningHours,
      suggestions,
      false,
      "openingHours",
    );
  };

  const renderContactName = () => {
    const suggestions = pickupAutocompleteData?.suggestions?.contactNames || [];
    return renderPickupAutocompleteField(
      "Contact Name:",
      contactName,
      setContactName,
      suggestions,
      false,
      "contactName",
    );
  };

  const renderPhoneNumber = () => {
    const suggestions = pickupAutocompleteData?.suggestions?.phoneNumbers || [];
    return renderPickupAutocompleteField(
      "Phone Number:",
      phoneNumber,
      setPhoneNumber,
      suggestions,
      false,
      "phoneNumber",
    );
  };

  const renderSupplierEmailContact = () => {
    const suggestions =
      pickupAutocompleteData?.suggestions?.supplierEmailContacts || [];
    return renderPickupAutocompleteField(
      "Email:",
      supplierEmailContact,
      setSupplierEmailContact,
      suggestions,
      false,
      "supplierEmailContact",
    );
  };

  const renderContentDetails = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Content Details (Product Category):"
          style={{ fontSize: 12, width: "250px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={contentDetails || ""}
          placeholder="Example: clothing, footwear…"
          handleChange={(e) => setContentDetails(e.target.value)}
          multiline
          rows={2}
          error={!contentDetails}
          helperText={!contentDetails && "Content details are required."}
        />
      </Stack>
    );
  };

  const renderPackingListNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Packing List Number:"
          style={{ fontSize: 12, width: "270px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={packingListNumber || ""}
          handleChange={(e) => setPackingListNumber(e.target.value)}
          error={!packingListNumber}
          helperText={!packingListNumber && "Packing list number is required."}
        />
      </Stack>
    );
  };

  const renderComments = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Comments:"
          style={{ fontSize: 12, width: "150px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={comments || ""}
          handleChange={(e) => setComments(e.target.value)}
        />
      </Stack>
    );
  };

  const renderReadyDateForPickup = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Ready Date for Pick-up:"
          style={{ fontSize: 12, width: "300px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={readyDateForPickup || ""}
          handleChange={(e) => setReadyDateForPickup(e.target.value)}
          error={!readyDateForPickup && !isDDPIncoterm}
          helperText={
            !readyDateForPickup &&
            !isDDPIncoterm &&
            "Ready date for pickup is required."
          }
        />
      </Stack>
    );
  };

  const renderPickUpConfirmationNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTextField
          label="Pick-up Confirmation Number"
          style={{ width: "100%" }}
          value={pickupConfirmationNumber || ""}
          handleChange={(e) => setPickupConfirmationNumber(e.target.value)}
        />
      </Stack>
    );
  };

  const renderTrackingNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTextField
          label="Tracking Number"
          style={{ width: "100%" }}
          value={trackingNumber || ""}
          handleChange={(e) => setTrackingNumber(e.target.value)}
        />
      </Stack>
    );
  };

  const renderPickupDetailsSection = () => {
    const renderDivider = (
      <div
        style={{
          width: "100%",
          border: "1px dashed #E6E6E6",
        }}
      />
    );

    return (
      <Stack
        style={{
          width: "95%",
          alignItems: "flex-start",
        }}
      >
        <Stack
          sx={{
            minWidth: "200px",
            width: { xs: "95%", md: "80%" },
            padding: "25px",
            border: "1px solid #E6E6E6",
          }}
        >
          <CustomTypography
            message="Shipping and delivery information"
            title
            style={{ marginBottom: "25px" }}
          />
          <Stack style={{ width: "100%", gap: 24 }}>
            <Stack style={{ gap: 25 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Shipment details"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>
                {renderDeliveryNumber()}
                {renderPackingListNumber()}
                {renderInvoiceNumber()}
                {renderTotalNumberOfCartons()}
                {renderTotalNumberOfPallets()}
                {renderBoxDimensions()}
                {renderPalletDimensions()}
                {renderTotalGrossWeight()}
                {renderTotalNetWeight()}
                {renderContentDetails()}
                {renderComments()}
              </Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Pickup location"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>{renderPickupCompanyName()}</Stack>
              <Stack>{renderPickupAddress()}</Stack>
              <Stack>{renderPickupZipCode()}</Stack>
              <Stack>{renderPickupCity()}</Stack>
              <Stack>{renderPickupCountry()}</Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Delivery address"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>{renderDeliveryAddress()}</Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Schedule and Availability"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>
                {renderOpeningHours()}
                {renderReadyDateForPickup()}
              </Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Contact Information"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>
                {renderContactName()}
                {renderPhoneNumber()}
                {renderSupplierEmailContact()}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <Seo title="Dashboard: New Inbound Request" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          padding: "10px 34px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          style={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <Stack style={{ gap: 10, width: "100%" }}>
            {renderBackButton()}
            {renderPOInfo()}
          </Stack>
          <Stack style={{ height: "80%" }}>
            {POVerificationLoading || !POIsValid || importZedonkParsing ? (
              (!selectPurchaseOrderModalVisible || importZedonkParsing) && (
                <Stack
                  style={{
                    height: "80%",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Stack
                    style={{
                      height: "80%",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 20,
                    }}
                  >
                    {importZedonkParsing && (
                      <CustomTypography
                        message="Importing Zedonk"
                        style={{ fontSize: 13 }}
                      />
                    )}
                    <CircularProgress
                      size={30}
                      style={{
                        color: "black",
                        alignSelf: "center",
                      }}
                    />
                  </Stack>
                </Stack>
              )
            ) : selectPurchaseOrderItemsModalVisible ? (
              renderSelectPurchaseOrderItemsSection()
            ) : (
              <Stack style={{ position: "relative" }}>
                {currentModalSection === "remaining_fields" &&
                  renderNewInboundRequestButtons()}
                {renderNewInboundRequestContent()}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
      {selectPurchaseOrderModalVisible && renderSelectPurchaseOrderModal()}
      {confirmIRModalOpened &&
        renderConfirmIRModal(
          () =>
            handleCreateInboundRequest(() => setConfirmIRModalOpened(false)),
          () => setConfirmIRModalOpened(false),
          `Are you sure you want to Submit this Inbound Request? You will not be able to edit it anymore.`,
          submitInboundRequestLoading,
        )}
    </>
  );
};

export default withInboundProcessGuard(AddInboundRequestPage);
